import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import orderService from '../services/orderService';
import websocketService from '../services/websocketService';
import audioService from '../services/audioService';
import { useAuth } from './AuthContext';
import { useMerchant } from './MerchantContext';
import { useMerchantData } from './MerchantDataContext';
import { useNotification } from './NotificationContext';
import { ORDER_STATUS_CONFIG } from '../config';

const OrderContext = createContext();

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

const STATUS_COLUMNS = [
  {
    key: 'pending',
    label: ORDER_STATUS_CONFIG.pending.label,
    color: '#1976d2',
    bgColor: '#e3f2fd',
    description: 'New orders waiting for preparation'
  },
  {
    key: 'preparing',
    label: ORDER_STATUS_CONFIG.preparing.label,
    color: '#1976d2',
    bgColor: '#e3f2fd',
    description: 'Orders currently being prepared'
  },
  {
    key: 'completed',
    label: ORDER_STATUS_CONFIG.completed.label,
    color: '#1976d2',
    bgColor: '#e3f2fd',
    description: 'Orders completed'
  }
];

export const OrderProvider = ({ children }) => {
  const { isAuthenticated, token } = useAuth();
  const { currentMerchant } = useMerchant();
  const { dataVersion } = useMerchantData();
  const { addNotification, handleWebSocketNotification } = useNotification();

  // Global order state
  const [ordersByStatus, setOrdersByStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wsConnectionStatus, setWsConnectionStatus] = useState('DISCONNECTED');
  const [, setRefreshInterval] = useState(null);
  const [updateCounter, setUpdateCounter] = useState(0); // Counter to force re-renders

  // Use refs for stable function references
  const addNotificationRef = useRef(addNotification);
  const handleWebSocketNotificationRef = useRef(handleWebSocketNotification);

  // Update refs when dependencies change
  useEffect(() => {
    addNotificationRef.current = addNotification;
  }, [addNotification]);

  useEffect(() => {
    handleWebSocketNotificationRef.current = handleWebSocketNotification;
  }, [handleWebSocketNotification]);

  // WebSocket event handlers with stable references
  const handleOrderUpdate = useCallback((data) => {
    const { order, action, skipNotification, play_sound } = data;

    if (!order) {
      return;
    }

    // Normalize order status to lowercase
    const orderStatus = order.status?.toLowerCase();

    setOrdersByStatus(prevOrdersByStatus => {
      const newOrdersByStatus = {};

      // Find which status the order currently lives in and its index
      let previousStatus = null;
      let previousIndex = -1;
      Object.keys(prevOrdersByStatus).forEach(status => {
        if (prevOrdersByStatus[status] && Array.isArray(prevOrdersByStatus[status])) {
          const idx = prevOrdersByStatus[status].findIndex(o => o.id === order.id);
          if (idx !== -1) {
            previousStatus = status;
            previousIndex = idx;
          }
        }
      });

      // Copy all status arrays, removing the order from wherever it was
      Object.keys(prevOrdersByStatus).forEach(status => {
        if (prevOrdersByStatus[status] && Array.isArray(prevOrdersByStatus[status])) {
          newOrdersByStatus[status] = prevOrdersByStatus[status].filter(o =>
            o.id !== order.id
          );
        } else {
          newOrdersByStatus[status] = [];
        }
      });

      // Add order to appropriate status column if not deleted
      // Skip orders with awaiting_payment status as they shouldn't appear on the board
      if (action !== 'deleted' && orderStatus && orderStatus !== 'awaiting_payment') {
        if (newOrdersByStatus[orderStatus] !== undefined) {
          const sameStatus = previousStatus === orderStatus;

          if (sameStatus && previousIndex !== -1) {
            // Order stayed in the same status (e.g. marked as paid) — keep its position
            newOrdersByStatus[orderStatus].splice(previousIndex, 0, order);
          } else {
            // New order or status changed — place at beginning (newest first)
            newOrdersByStatus[orderStatus] = [order, ...newOrdersByStatus[orderStatus]];
          }
        }
      }

      return newOrdersByStatus;
    });

    // Increment update counter to force re-renders
    setUpdateCounter(prev => prev + 1);

    // Toast vs. sound are decoupled:
    //  - A brand-new order (action 'created') raises the announcement toast,
    //    and chimes too when the backend asks (play_sound).
    //  - Any other change that flags play_sound (e.g. a transition to
    //    'preparing') only chimes — no toast — so a manual status change
    //    doesn't stack a second toast on the page's own confirmation.
    const isOnAdminPage = window.location.pathname.startsWith('/admin');
    if (isOnAdminPage) {
      if (!skipNotification && action === 'created') {
        const statusText = orderStatus === 'pending' ? 'New' : orderStatus === 'preparing' ? 'Preparing' : 'Updated';
        addNotificationRef.current(`${statusText} order #${order.order_number}`, 'info', 6000, Boolean(play_sound));
      } else if (play_sound) {
        audioService.playNotificationSound();
      }
    }
  }, []);

  const handleWebSocketConnected = useCallback(() => {
    setWsConnectionStatus('CONNECTED');
  }, []);

  const handleWebSocketDisconnected = useCallback(() => {
    setWsConnectionStatus('DISCONNECTED');
  }, []);

  const handleWebSocketError = useCallback(() => {
    setWsConnectionStatus('ERROR');
  }, []);

  const handleAdminNotification = useCallback(async (data) => {
    const { notification } = data;

    if (!notification) {
      return;
    }

    // Only show notifications with sound when on admin pages
    if (!window.location.pathname.startsWith('/admin')) {
      return;
    }

    // Only add to persistent notifications dropdown
    handleWebSocketNotificationRef.current({
      id: notification.id || Date.now(),
      title: notification.title,
      message: notification.message || notification.title,
      notification_type: notification.notification_type || 'order_notification'
    });

    // If this is an order_created notification, fetch and add the order to the list
    if (notification.type === 'order_created' && notification.data?.order_id) {
      try {
        const orderService = (await import('../services/orderService')).default;
        const order = await orderService.getOrder(notification.data.order_id);

        // Add the order to the state using the handler from ref
        if (handlersRef.current && handlersRef.current.handleOrderUpdate) {
          handlersRef.current.handleOrderUpdate({
            order,
            action: 'created',
            skipNotification: true
          });
        }
      } catch (error) {
        // Failed to fetch order, notification already shown
      }
    }
  }, []);

  // Guards the board fetch so the 30s fallback poll can never stack a second
  // request on top of one still in flight. Holds the start timestamp of the
  // active request (0 = idle); a request older than the stale cutoff no
  // longer blocks new fetches, so a hung connection (the api client sets no
  // timeout) can't wedge polling until a page refresh.
  const fetchStartedAtRef = useRef(0);
  const FETCH_STALE_AFTER_MS = 90000;

  const isFetchInFlight = () => {
    return (
      fetchStartedAtRef.current !== 0 &&
      Date.now() - fetchStartedAtRef.current < FETCH_STALE_AFTER_MS
    );
  };

  // Claims the guard for a new request and returns its ID (the start
  // timestamp — it doubles as the staleness clock above). Two live requests
  // can never share an ID: a second fetch only begins once the first is at
  // least FETCH_STALE_AFTER_MS old.
  const beginFetch = () => {
    const fetchId = Date.now();
    fetchStartedAtRef.current = fetchId;
    return fetchId;
  };

  // True while `fetchId` still owns the guard. A request superseded by a
  // newer one loses ownership: it must not apply its results to the board
  // nor release the guard the newer request now holds.
  const isCurrentFetch = (fetchId) => fetchStartedAtRef.current === fetchId;

  const endFetch = (fetchId) => {
    if (isCurrentFetch(fetchId)) {
      fetchStartedAtRef.current = 0;
    }
  };

  // Fetch orders by status
  const fetchOrdersByStatus = useCallback(async () => {
    if (isFetchInFlight()) {
      return;
    }
    const fetchId = beginFetch();
    setLoading(true);
    try {
      const response = await orderService.getOrdersByStatus();

      if (isCurrentFetch(fetchId)) {
        // Initialize empty status groups
        const initialOrdersByStatus = {};
        STATUS_COLUMNS.forEach(column => {
          initialOrdersByStatus[column.key] = [];
        });

        // Populate with actual data (backend already filters out online payment orders)
        if (response && typeof response === 'object') {
          Object.keys(response).forEach(status => {
            const statusData = response[status];
            if (statusData && statusData.orders && Array.isArray(statusData.orders)) {
              initialOrdersByStatus[status] = statusData.orders;
            }
          });
        }

        setOrdersByStatus(initialOrdersByStatus);
        setError(null);
      }
    } catch (err) {
      if (isCurrentFetch(fetchId)) {
        setError('Failed to load orders. Please try again later.');

        // Initialize empty status groups on error
        const emptyOrdersByStatus = {};
        STATUS_COLUMNS.forEach(column => {
          emptyOrdersByStatus[column.key] = [];
        });
        setOrdersByStatus(emptyOrdersByStatus);
      }
    } finally {
      endFetch(fetchId);
      setLoading(false);
    }
  }, []);

  // Store stable references for WebSocket handlers
  const handlersRef = useRef({
    handleOrderUpdate,
    handleAdminNotification,
    handleWebSocketConnected,
    handleWebSocketDisconnected,
    handleWebSocketError
  });

  // Update handlers ref when they change
  useEffect(() => {
    handlersRef.current = {
      handleOrderUpdate,
      handleAdminNotification,
      handleWebSocketConnected,
      handleWebSocketDisconnected,
      handleWebSocketError
    };
  });

  // Initialize WebSocket connection
  const merchantId = currentMerchant?.id;
  useEffect(() => {
    if (isAuthenticated() && merchantId) {
      // Set connection status to connecting
      setWsConnectionStatus('CONNECTING');

      // Set up WebSocket listeners using stable references
      const handlers = handlersRef.current;

      websocketService.addListener('orderUpdate', handlers.handleOrderUpdate);
      websocketService.addListener('adminNotification', handlers.handleAdminNotification);
      websocketService.addListener('connected', handlers.handleWebSocketConnected);
      websocketService.addListener('disconnected', handlers.handleWebSocketDisconnected);
      websocketService.addListener('error', handlers.handleWebSocketError);

      // Connect to WebSocket (authentication via cookies)
      websocketService.connect(merchantId);

      return () => {
        // Clean up listeners using the same references
        websocketService.removeListener('orderUpdate', handlers.handleOrderUpdate);
        websocketService.removeListener('adminNotification', handlers.handleAdminNotification);
        websocketService.removeListener('connected', handlers.handleWebSocketConnected);
        websocketService.removeListener('disconnected', handlers.handleWebSocketDisconnected);
        websocketService.removeListener('error', handlers.handleWebSocketError);

        // Disconnect WebSocket
        websocketService.disconnect();
      };
    } else {
      setWsConnectionStatus('DISCONNECTED');
    }
  }, [isAuthenticated, merchantId, token]);

  // Clear orders and refetch when merchant changes (via dataVersion)
  useEffect(() => {
    if (isAuthenticated() && merchantId) {
      // Clear existing orders
      const emptyOrdersByStatus = {};
      STATUS_COLUMNS.forEach(column => {
        emptyOrdersByStatus[column.key] = [];
      });
      setOrdersByStatus(emptyOrdersByStatus);

      // Set loading state
      setLoading(true);

      // Refetch orders for new merchant
      fetchOrdersByStatus();
    }
  }, [dataVersion, isAuthenticated, merchantId, fetchOrdersByStatus]);

  // Set up auto-refresh as fallback when WebSocket is not connected
  useEffect(() => {
    // Set up auto-refresh every 30 seconds (as fallback)
    const interval = setInterval(async () => {
      // Only auto-refresh if WebSocket is not connected, and never while a
      // fetch is still running — a slow response would otherwise pile up a
      // new request every 30s.
      if (wsConnectionStatus !== 'CONNECTED' && !isFetchInFlight()) {
        const fetchId = beginFetch();
        try {
          const response = await orderService.getOrdersByStatus();

          if (isCurrentFetch(fetchId)) {
            // Initialize empty status groups
            const initialOrdersByStatus = {};
            STATUS_COLUMNS.forEach(column => {
              initialOrdersByStatus[column.key] = [];
            });

            // Populate with actual data
            if (response && typeof response === 'object') {
              Object.keys(response).forEach(status => {
                const statusData = response[status];
                if (statusData && statusData.orders && Array.isArray(statusData.orders)) {
                  initialOrdersByStatus[status] = statusData.orders;
                }
              });
            }

            setOrdersByStatus(initialOrdersByStatus);
            setError(null);
          }
        } catch (err) {
          // Auto-refresh failed, will retry on next interval
        } finally {
          endFetch(fetchId);
        }
      }
    }, 30000);

    setRefreshInterval(interval);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [wsConnectionStatus]);

  // Helper functions
  const getTotalOrdersCount = useCallback(() => {
    return Object.values(ordersByStatus).reduce((total, orders) => total + orders.length, 0);
  }, [ordersByStatus]);

  const getActiveOrders = useCallback(() => {
    const activeStatuses = ['pending', 'preparing', 'ready'];
    return Object.entries(ordersByStatus)
      .filter(([status]) => activeStatuses.includes(status))
      .flatMap(([, orders]) => orders);
  }, [ordersByStatus]);

  const getCompletedOrders = useCallback(() => {
    const completedStatuses = ['completed', 'cancelled'];
    return Object.entries(ordersByStatus)
      .filter(([status]) => completedStatuses.includes(status))
      .flatMap(([, orders]) => orders);
  }, [ordersByStatus]);

  // Context value
  const contextValue = {
    // State
    ordersByStatus,
    loading,
    error,
    wsConnectionStatus,
    statusColumns: STATUS_COLUMNS,
    updateCounter, // Add counter for components to detect updates

    // Functions
    fetchOrdersByStatus,
    getTotalOrdersCount,
    getActiveOrders,
    getCompletedOrders,
    updateOrder: handleOrderUpdate,

    // WebSocket status
    isWebSocketConnected: wsConnectionStatus === 'CONNECTED'
  };

  return (
    <OrderContext.Provider value={contextValue}>
      {children}
    </OrderContext.Provider>
  );
};
